<template>
  <div
    :style="{ height: `${height}px` }"
  >
    <div
      class="h5-checkout"
      ref="checkoutRef"
    >
      <AdaptiveIntercomFooter class="h5-checkout__placeholder">
        <CouponDetail
          class="h5-checkout__coupon"
          :disabled="!discountAmountUSD"
        />
        <BulkDiscount class="h5-checkout__discount" />
        <div class="h5-checkout-popup">
          <div
            ref="checkoutMainRef"
            class="h5-checkout-popup__main"
            :class="{
              'h5-checkout-popup__empty': checkableCartList.length === 0
            }"
          >
            <div
              class="h5-checkout-popup__all"
              v-if="checkableCartList.length > 0"
            >
              <CartsSelect
                class="h5-checkout-popup__select"
                :selected="isCheckedAll"
                @click="handleCheckAll"
              />
              All
            </div>
            <div class="h5-checkout-popup__totals">
              <div
                class="h5-checkout-popup-total"
              >
                Total Amount
                <IntlCurrency
                  class="h5-checkout-popup-total__currency"
                  currency="USD"
                />
                <span class="h5-checkout-popup-total__amount">{{ totalAmountAfterDiscount }}</span>
                <Icon
                  v-if="selectedSkuCount > 0"
                  class="h5-checkout-popup-total__arrow"
                  name="arrow-down-l-2"
                  @click="onOpen"
                />
              </div>
              <div
                class="h5-checkout-popup-saved"
                v-if="discountAmountUSD"
              >
                <div class="h5-checkout-popup-saved__applied">
                  <Icon
                    class="h5-checkout-popup-saved__icon"
                    name="coupon-f"
                  />
                  Applied
                </div>
                <p>
                  Saved <IntlCurrency currency="USD" />
                  {{ discountAmountUSD.toFixed(2) }}
                </p>
              </div>
            </div>
          </div>
          <div
            class="h5-checkout-popup__buttons"
            v-show="!disabled"
          >
            <PayButton />
          </div>
          <div
            class="h5-checkout-processing"
            v-if="hasProcessingSelected"
          >
            <p class="h5-checkout-processing__title">
              <Icon
                class="h5-checkout-processing__icon"
                name="icon-warning-outline"
              />Pending Processing ...
            </p>
            <p class="h5-checkout-processing__desc">
              The platform will complete the processing within 1 business day. We have detected that some items require shipping cost confirmation. Please be patient.
              &nbsp;<span @click="intercom.showNewMessage()">Free Expert Consultation Now!</span>
            </p>
          </div>
          <div
            class="h5-checkout-undeliverable"
            v-else-if="hasUndeliverableSelected"
          >
            <p class="h5-checkout-undeliverable__title">
              Undeliverable Logistics
            </p>
            <p class="h5-checkout-undeliverable__desc">
              The logistics cannot be delivered at the moment. Please contact customer service for more information!
              &nbsp;<span @click="intercom.showNewMessage()">Contact us</span>
            </p>
          </div>
        </div>
      </AdaptiveIntercomFooter>
    </div>
    <Teleport to="body">
      <div
        class="h5-checkout__detail"
        ref="checkDetailRef"
      >
        <div
          class="h5-checkout__detail-wrapper"
          :class="{
            'h5-checkout__detail-wrapper--divider': !hasProcessingSelected && !hasUndeliverableSelected
          }"
        >
          <CheckoutDetail @close="onClose" />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { useElementSize } from '@vueuse/core'
import { AdaptiveIntercomFooter } from '@/components/AdaptiveIntercomFooter'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import { Icon } from '@/components/Icon'
import CartsSelect from '@/views/Carts/components/CartsSelect.vue'
import { useIntercom } from '@/hooks/useIntercom'
import { useCart } from '@/views/Carts/hooks/useCart'
import PayButton from '@/views/Carts/components/PayButton.vue'
import { useOrder } from '@/views/Carts/hooks/useOrder'
import BulkDiscount from './BulkDiscount.vue'
import CouponDetail from './CouponDetail.vue'
import CheckoutDetail from './CheckoutDetail.vue'

const intercom = useIntercom()

const { cartList, isCheckedAll, handleCheckAll } = useCart()

const checkableCartList = computed(() => {
  return cartList.value.filter((item) => !item.disabled)
})

const { selectedSkuCount, hasProcessingSelected, hasUndeliverableSelected, discountAmountUSD, totalAmountAfterDiscount } = useOrder()

const disabled = computed(() => hasProcessingSelected.value || hasUndeliverableSelected.value)

const checkoutRef = useTemplateRef('checkoutRef')
const checkoutMainRef = useTemplateRef('checkoutMainRef')
const checkDetailRef = useTemplateRef('checkDetailRef')
const { height } = useElementSize(checkoutRef)

const onOpen = () => {
  if (checkoutMainRef.value == null || checkDetailRef.value == null) return
  const { bottom } = checkoutMainRef.value.getBoundingClientRect()
  checkDetailRef.value.style.bottom = `${window.innerHeight - bottom}px`
  checkDetailRef.value.style.height = `${bottom - 72}px`
}

const onClose = () => {
  checkDetailRef.value.style.height = 0
}
</script>

<style lang="less" scoped>
.h5-checkout {
  z-index: 10;
  position: fixed;
  right: 12px;
  bottom: 11px;
  left: 12px;

  &__placeholder {
    height: 100%;
  }

  &__coupon {
    margin: 0 12px 8px;
  }

  &__discount {
    margin-bottom: -7px;
    padding-bottom: 15px;
  }

  &-popup {
    z-index: 2;
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 16px 12px;
    height: 100%;
    border-radius: 8px;
    background-color: #141414;
    color: #FFFFFF;

    &__main {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 20px;
    }

    &__all {
      display: flex;
      align-items: center;
      gap: 6px;
      font-family: mon-500;
      font-size: 14px;
      line-height: 18px;
    }

    &__select {
      font-size: 20px;
    }

    &__empty {
      .h5-checkout-popup {
        &__totals {
          width: 100%;
        }

        &-total__currency {
          margin-left: auto;
        }
      }
    }

    &-total {
      display: flex;
      align-items: center;
      font-family: mon-600;
      font-size: 14px;
      line-height: 18px;

      &__currency {
        margin-left: 8px;
        font-size: 18px;
      }

      &__amount {
        font-family: mon-700;
        font-size: 18px;
        line-height: 22px;
      }

      &__arrow {
        margin-left: 4px;
        font-size: 16px;
      }
    }

    &-saved {
      margin-top: 4px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 6px;
      font-family: mon-600;
      font-size: 11px;
      line-height: 14px;
      color: #C2E7F7;

      &__applied {
        display: flex;
        align-items: center;
        gap: 2px;
        padding: 1px 6px 1px 4px;
        background-color: #FEF1F0;
        border-radius: 2px;
        font-family: mon-700;
        font-style: italic;
        color: #F54550;
      }

      &__icon {
        font-size: 14px;
      }
    }
  }

  &__detail {
    z-index: 1102;
    position: fixed;
    right: 12px;
    left: 12px;
    height: 0;
    overflow: hidden;
    background-color: #141414;
    color: #FFFFFF;
    transition: height .2s ease-in-out;

    &-wrapper {
      height: 100%;
      padding: 16px 12px;
      border-radius: 8px 8px 0 0;
      overflow-y: auto;
      overflow-x: hidden;

      &--divider {
        &::after {
          content: '';
          position: absolute;
          bottom: 16px;
          right: 12px;
          left: 12px;
          height: 1px;
          background-color:  #232323;
        }
      }
    }
  }

  &-processing {
    padding-top: 12px;
    border-top: 1px solid #232323;

    &__title {
      font-family: mon-600;
      font-size: 11px;
      line-height: 14px;
      color: #FFA033;
    }

    &__icon {
      margin-right: 4px;
      font-size: 14px;
    }

    &__desc {
      margin-top: 6px;
      font-family: mon-500;
      font-size: 10px;
      line-height: 14px;

      > span {
        font-family: mon-600;
        text-decoration: underline;
        color: #009DE0;
      }
    }
  }

  &-undeliverable {
    padding-top: 12px;
    border-top: 1px solid #232323;

     &__title {
      font-family: mon-600;
      font-size: 11px;
      line-height: 14px;
      color: #FA9292;
    }

    &__desc {
      margin-top: 6px;
      font-family: mon-500;
      font-size: 12px;
      line-height: 16px;
      color: #F7E3E8;

      > span {
        font-family: mon-600;
        text-decoration: underline;
        color: #FFFFFF;
      }
    }
  }
}
</style>
